import { createSlice } from '@reduxjs/toolkit';
import { readStoredAiModel, storeAiModel } from 'constants/aiModels';

export interface DataChangedNotice {
	scope: 'costAllocation' | 'expenseAllocation' | 'timeActivity' | 'configurationCustomRules';
	companyId: string;
	payPeriodId?: string;
	// The single cell a write changed, when it was a single-cell write. Lets a
	// listening table paint the new value immediately instead of waiting out the
	// refetch that follows — the refetch is still what reconciles section totals
	// and labor burden, so this is an optimistic patch, not a replacement for it.
	// Absent for bulk writes, where there is no one cell to patch.
	cell?: { employeeId: string; fieldId: string; value: string };
	timestamp: number;
}

const initialState = {
	pendingMessage: null as string | null,
	nonce: 0,
	// "New chat" asks for the panel without a question attached. Kept as its own
	// counter so an open-only request never looks like a message send to the
	// nonce/pendingMessage effect that drives askAssistant.
	openNonce: 0,
	// Which Claude model answers. Set by the switcher on the dashboard's chat
	// surface and read by ChatBubble, which is what actually calls /ai/chat —
	// seeded from the last choice so it survives a reload.
	model: readStoredAiModel(),
	dataChanged: null as DataChangedNotice | null,
	// A saved thread the sidebar asked to reopen. Paired with loadNonce (a
	// counter) so re-selecting the same thread still triggers a fresh load —
	// same pattern as pendingMessage/nonce.
	conversationToLoad: null as string | null,
	loadNonce: 0,
	// Bumped by ChatBubble whenever a turn is persisted (a thread was created or
	// its title/timestamp changed). The sidebar's thread list watches this to
	// refetch, so a new chat appears without a reload.
	conversationsVersion: 0,
	// Conversations whose reply arrived while the user wasn't looking at them —
	// they navigated away from /chat before it finished. The sidebar shows a blue
	// unread dot on these; opening the thread clears it. A map for O(1) add/remove.
	unreadConversationIds: {} as Record<string, true>,
	// Conversations whose reply is currently generating — the sidebar shows a
	// spinner on each. Keyed per-conversation (not a single value) so a running
	// thread keeps its spinner even after the user navigates away, and it's
	// cleared only when THAT reply finishes. A brand-new chat has no id yet, so it
	// lives in pendingNewChat until the backend assigns one (then it's adopted
	// here). Map for O(1) add/remove.
	runningConversationIds: {} as Record<string, true>,
	// A brand-new chat generating its first reply, before the backend has saved it
	// and handed back an id — shown as a temporary "working" row at the top. Moved
	// into runningConversationIds once its id arrives (ai_chat_user_saved).
	pendingNewChat: null as { title: string } | null,
	// The thread currently open on the /chat page, so the sidebar can show it as
	// selected. Set when a thread is opened or a new one is created, cleared on
	// "New chat".
	activeConversationId: null as string | null,
};

const chatBubbleSlice = createSlice({
	initialState,
	name: 'ChatBubble',
	reducers: {
		askAssistant: (state, action) => {
			state.pendingMessage = action.payload;
			state.nonce += 1;
		},
		clearPendingMessage: (state) => {
			state.pendingMessage = null;
		},
		setChatModel: (state, action: { payload: string }) => {
			state.model = action.payload;
			storeAiModel(action.payload);
		},
		// Opens the assistant on an empty thread — the sidebar's "New chat".
		// Any question still queued from a previous hand-off is dropped, since
		// a new chat is precisely a request not to carry anything over — and any
		// thread queued for reopening is cleared for the same reason.
		startNewChat: (state) => {
			state.openNonce += 1;
			state.pendingMessage = null;
			state.conversationToLoad = null;
			state.activeConversationId = null;
		},
		// The sidebar asked to reopen a saved thread. ChatBubble watches loadNonce,
		// fetches the transcript and shows it on the /chat page. The selected row
		// highlights immediately (optimistic), before the transcript even loads.
		openConversation: (state, action: { payload: string }) => {
			state.conversationToLoad = action.payload;
			state.loadNonce += 1;
			state.pendingMessage = null;
			state.activeConversationId = action.payload;
		},
		// Signals the thread list to refetch (a turn was just persisted).
		bumpConversations: (state) => {
			state.conversationsVersion += 1;
		},
		// The thread ChatBubble is currently in — set when a brand-new thread is
		// created from the composer, so the sidebar highlights it too.
		setActiveConversation: (state, action: { payload: string | null }) => {
			state.activeConversationId = action.payload;
		},
		// A reply started generating. An existing thread (id known) gets a spinner
		// on its row; a brand-new chat (no id yet) shows the temporary row.
		startRunningConversation: (state, action: { payload: { conversationId: string | null; title: string } }) => {
			if (action.payload.conversationId) {
				state.runningConversationIds[action.payload.conversationId] = true;
			} else {
				state.pendingNewChat = { title: action.payload.title };
			}
		},
		// The backend saved the brand-new chat and handed back its id — mark that
		// thread as running so its real sidebar row carries the spinner. The
		// temporary pendingNewChat row is NOT cleared here: its replacement (the
		// real row) only appears after the sidebar refetches, so clearing it now
		// would blink the loader off during that gap. ChatNav drops the temporary
		// row the moment the real running row is actually in the list.
		adoptRunningConversation: (state, action: { payload: string }) => {
			state.runningConversationIds[action.payload] = true;
		},
		// That conversation's reply finished (or failed) — drop its spinner. Runs
		// regardless of which chat the user is now viewing.
		endRunningConversation: (state, action: { payload: string | null }) => {
			if (action.payload) {
				delete state.runningConversationIds[action.payload];
			}
		},
		clearPendingNewChat: (state) => {
			state.pendingNewChat = null;
		},
		// A reply landed for a thread the user isn't currently viewing.
		markConversationUnread: (state, action: { payload: string }) => {
			state.unreadConversationIds[action.payload] = true;
		},
		// Cleared when the thread is opened/viewed.
		clearConversationUnread: (state, action: { payload: string }) => {
			delete state.unreadConversationIds[action.payload];
		},
		// Tells any currently-mounted table that an AI-chat-confirmed write may
		// have changed its data, without ChatBubble having to know that table's
		// current filters/sort/page — the table re-runs its OWN existing fetch
		// on a matching notice instead of ChatBubble guessing the right query.
		notifyDataChanged: (state, action: { payload: Omit<DataChangedNotice, 'timestamp'> }) => {
			state.dataChanged = { ...action.payload, timestamp: Date.now() };
		},
	},
});

export default chatBubbleSlice;
export const {
	askAssistant,
	clearPendingMessage,
	setChatModel,
	startNewChat,
	notifyDataChanged,
	openConversation,
	bumpConversations,
	setActiveConversation,
	markConversationUnread,
	clearConversationUnread,
	startRunningConversation,
	adoptRunningConversation,
	endRunningConversation,
	clearPendingNewChat,
} = chatBubbleSlice.actions;
